<template>
  <v-container fluid class="pa-0">
    <SharedTableSkeleton
      v-if="showSkeletonLoading"
      :row-count="itemsPerPage || 5"
      :column-count="4"
      column-template="1fr 220px 200px 320px"
    />
    <UniversalTable
      v-else
      class="employees-directory-table"
      :config="tableConfig.tableParams.value"
      :items="filteredEmployees"
      :backendPagination="backendPagination"
      :itemsCount="itemsCount"
      :externalCurrentPage="currentPage"
      :itemsPerPage="itemsPerPage"
      @onSearch="onSearch"
      @onRequest="$emit('onRequest', $event)"
      @onClick="viewState.state.filters.showFilterDialog = true"
      @removeFilter="viewState.removeFilters"
      @removeAllFilters="viewState.clearFilters"
      :emptyObjectData="emptyObjectData"
    />
    <SearchModal
      :dialog="viewState.state.filters.showFilterDialog"
      @back="viewState.state.filters.showFilterDialog = false"
      @checkedItemsChanged="viewState.applyFilters"
      :clearSelection="viewState.state.filters.clearSelection"
      filterType="Directory Employees"
      :filterGroups="[
        {
          label: 'Readiness',
          items: [
            { label: 'No Filter', value: 'no_filter' },
            { label: 'Developing', value: 'developing' },
            { label: 'Competent', value: 'competent' },
            { label: 'Expert', value: 'expert' },
          ],
        },
      ]"
      :statusOptions="[]"
      :modalWidth="800"
      :defaultSelected="viewState.state.filters.values"
    />
  </v-container>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";
import UniversalTable from "@components/UniversalTable/UniversalTable.vue";
import SharedTableSkeleton from "@components/SharedTableSkeleton/SharedTableSkeleton.vue";
import SearchModal from "@components/SearchBar/SearchModal.vue";
import { LD_FLAG_IDEA_MDP_560 } from "@components/constants/launchDarklyFlagKeys";
import { useTableConfig } from "./composables/useTableConfig";
import { useEmployeesDirectoryTableViewState } from "./composables/useViewState";
import { matchesReadinessFilter } from "./composables/readinessFilter.js";

const props = defineProps({
  dataSource: {
    type: Array,
    default: () => [],
  },
  backendPagination: {
    type: Boolean,
    default: false,
  },
  itemsCount: {
    type: Number,
    default: 0,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  itemsPerPage: {
    type: Number,
    default: 20,
  },
  searchText: {
    type: String,
    default: "",
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["onRequest", "onSearch"]);

const proxy = getCurrentInstance()?.proxy;

// ER-10603 / MDP-560 — gate skeleton loading behind the consistent-skeleton feature flag.
const skeletonEnabled = ref(false);
const showSkeletonLoading = computed(
  () => props.loading && skeletonEnabled.value
);

onMounted(async () => {
  try {
    skeletonEnabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_560, false)) === true;
  } catch {
    skeletonEnabled.value = false;
  }
});

const viewState = useEmployeesDirectoryTableViewState();
const tableConfig = useTableConfig(viewState);

watch(
  () => props.searchText,
  (val) => {
    viewState.state.filters.searchText = val ?? "";
  },
  { immediate: true }
);

function onSearch(text) {
  viewState.state.filters.searchText = text ?? "";
  emit("onSearch", text);
}

const emptyObjectData = {
  singular: "Employee",
  plural: "Employees",
};

const NO_FILTER_LABEL = "No Filter";
const NO_FILTER_VALUE = "no_filter";

const filteredEmployees = computed(() => {
  let employees = props.dataSource.filter(
    (el) => el?.current_status === "active"
  );

  if (!props.backendPagination) {
    const searchText = viewState.state.filters.searchText?.trim();
    if (searchText) {
      const query = searchText.toLowerCase();
      employees = employees.filter((el) => {
        const first = (el?.first_name ?? "").toLowerCase();
        const last = (el?.last_name ?? "").toLowerCase();
        const fullName = `${first} ${last}`.trim();
        return fullName.includes(query) || first.includes(query);
      });
    }
  }

  const filterValues = viewState.state.filters.values;
  if (filterValues.length > 0) {
    const readinessFilter = filterValues[0];
    const isNoFilter =
      readinessFilter === NO_FILTER_LABEL ||
      readinessFilter === NO_FILTER_VALUE;
    if (!isNoFilter) {
      employees = employees.filter((el) =>
        matchesReadinessFilter(el, readinessFilter)
      );
    }
  }

  return employees;
});
</script>

<style scoped>
:deep(.filter-button) {
  /* Override SearchBar .filter-button { padding-left: unset !important } */
  padding-left: 16px !important;
}

.employees-directory-table ::v-deep thead th:not(:first-child) .sortable-header-content,
.employees-directory-table ::v-deep thead th:not(:first-child) .d-flex.align-center {
  justify-content: center !important;
}

.employees-directory-table ::v-deep tbody td:not(:first-child) {
  text-align: center !important;
}
.icon-container {
  cursor: pointer;
}
:deep(.employee-badge-counter) {
  margin: 0 auto !important;
}
.employees-directory-table ::v-deep tbody tr:hover {
  background-color: transparent !important;
}
</style>
